// FECOA-1270 — participant offline retry queue.
//
// When save_response fails because the device is offline, the answer is
// enqueued here (encrypted at rest — see secureStore.js) and flushed through
// POST /api/survey-participant-responses/sync_batch/ when connectivity
// returns. The server applies accept-and-flag: answers are never rejected for
// window expiry, only flagged, so the queue's job is purely not to lose data.
// Per-item results are walked so only failures stay queued; 'saved' and
// 'stale' (a newer answer already exists server-side) both clear the row.
//
// The queue is device-bound, not session-bound: rows are keyed by participant
// id and survive logout/token expiry, because a participant whose token
// expires while offline must not lose answers. Only rows belonging to the
// currently authenticated participant are ever flushed.

import api from '../api';
import * as secureStore from './secureStore';

const SYNC_ENDPOINT = '/api/survey-participant-responses/sync_batch/';

let store = secureStore;
let memoryFallback = null;

// Some private-browsing modes reject IndexedDB entirely. Fall back to an
// in-memory queue for the life of the tab rather than dropping answers.
const createMemoryStore = () => {
  let nextId = 1;
  const rows = new Map();
  return {
    addRow: async ({ participantId, assignmentId, kind, payload }) => {
      const id = nextId++;
      rows.set(id, {
        id,
        participantId: String(participantId),
        assignmentId: String(assignmentId),
        kind,
        queuedAt: Date.now(),
        payload,
      });
      return id;
    },
    getRows: async (participantId) =>
      [...rows.values()]
        .filter((r) => r.participantId === String(participantId))
        .map(({ participantId: _p, ...rest }) => ({ ...rest })),
    countRows: async (participantId) =>
      [...rows.values()].filter((r) => r.participantId === String(participantId)).length,
    deleteRows: async (ids) => {
      for (const id of ids) rows.delete(id);
    },
    clearParticipant: async (participantId) => {
      for (const [id, r] of rows) {
        if (r.participantId === String(participantId)) rows.delete(id);
      }
    },
  };
};

const withStore = async (fn) => {
  try {
    return await fn(store);
  } catch (err) {
    if (store !== secureStore) throw err;
    console.warn('[OFFLINE QUEUE] Encrypted store unavailable, using in-memory fallback:', err?.message);
    memoryFallback = memoryFallback || createMemoryStore();
    store = memoryFallback;
    return fn(store);
  }
};

// The participant user object (persisted by storeParticipantSession) is the
// session marker: it carries participant_number, which staff users never have.
export const getActiveParticipantId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.id != null && user?.participant_number != null) return String(user.id);
    return null;
  } catch {
    return null;
  }
};

// ── Listeners ───────────────────────────────────────────────────────────────

const listeners = new Set();
let lastState = { pending: 0, syncing: false, terminated: false };

const notify = (partial) => {
  lastState = { ...lastState, ...partial };
  for (const cb of listeners) {
    try {
      cb(lastState);
    } catch (err) {
      console.error('[OFFLINE QUEUE] Listener error:', err);
    }
  }
};

export const subscribe = (cb) => {
  listeners.add(cb);
  cb(lastState);
  return () => listeners.delete(cb);
};

const refreshPending = async () => {
  const participantId = getActiveParticipantId();
  if (!participantId) return 0;
  const pending = await withStore((s) => s.countRows(participantId)).catch(() => 0);
  notify({ pending });
  return pending;
};

// ── Enqueue ─────────────────────────────────────────────────────────────────

// Note: re-answering the same question offline creates a second row rather
// than replacing the first (question ids live inside the ciphertext, so
// dedupe would mean decrypting the queue on every enqueue). The server's
// latest-answer-wins conflict rule resolves it at sync time and both rows
// clear together.
export const enqueueResponse = async ({ assignmentId, questionId, responseData, clientAnsweredAt }) => {
  const participantId = getActiveParticipantId();
  if (!participantId) throw new Error('No participant session');
  await withStore((s) =>
    s.addRow({
      participantId,
      assignmentId,
      kind: 'response',
      payload: {
        question_id: questionId,
        response_data: responseData,
        client_answered_at: clientAnsweredAt,
      },
    })
  );
  await refreshPending();
};

export const enqueueSubmit = async ({ assignmentId, skippedQuestions }) => {
  const participantId = getActiveParticipantId();
  if (!participantId) throw new Error('No participant session');
  const rows = await withStore((s) => s.getRows(participantId));
  const forAssignment = rows.filter((r) => String(r.assignmentId) === String(assignmentId));
  // One submit intent per assignment — replace any earlier one.
  const staleSubmits = forAssignment.filter((r) => r.kind === 'submit').map((r) => r.id);
  if (staleSubmits.length) await withStore((s) => s.deleteRows(staleSubmits));
  await withStore((s) =>
    s.addRow({
      participantId,
      assignmentId,
      kind: 'submit',
      payload: { skipped_questions: skippedQuestions || [] },
    })
  );
  await refreshPending();
};

export const getPendingCount = async () => {
  const participantId = getActiveParticipantId();
  if (!participantId) return 0;
  return withStore((s) => s.countRows(participantId)).catch(() => 0);
};

export const hasPendingResponsesForAssignment = async (assignmentId) => {
  const participantId = getActiveParticipantId();
  if (!participantId) return false;
  const rows = await withStore((s) => s.getRows(participantId)).catch(() => []);
  return rows.some((r) => String(r.assignmentId) === String(assignmentId) && r.kind === 'response');
};

// ── Flush ───────────────────────────────────────────────────────────────────

// Walk sync_batch per-item results against the queued response rows.
// 'saved' and 'stale' clear every row for that question; 'failed' rows stay
// queued. Exported for tests.
export const partitionSyncResults = (responseRows, results) => {
  const resolvedQuestionIds = new Set();
  const failedQuestionIds = new Set();
  for (const item of results || []) {
    if (item.status === 'saved' || item.status === 'stale') {
      resolvedQuestionIds.add(String(item.question_id));
    } else {
      failedQuestionIds.add(String(item.question_id));
    }
  }
  const resolvedRowIds = responseRows
    .filter((r) => resolvedQuestionIds.has(String(r.payload.question_id)))
    .map((r) => r.id);
  return { resolvedRowIds, failedQuestionIds: [...failedQuestionIds] };
};

// Assignments the server hard-blocked this app session (survey deactivated,
// timepoint ordering, assignment not found). Rows are kept — the block may be
// operator-fixable — but re-flushing every cycle would spam the backend.
const blockedAssignments = new Set();

let flushInFlight = null;

const flushAssignment = async (participantId, assignmentId, rows) => {
  const responseRows = rows
    .filter((r) => r.kind === 'response')
    .sort((a, b) => a.queuedAt - b.queuedAt || a.id - b.id);
  const submitRow = rows.find((r) => r.kind === 'submit');

  if (!responseRows.length) {
    // sync_batch requires a non-empty responses list, so a lone submit row can
    // never flush. SurveyTaking always seeds one response alongside a queued
    // submit; if that invariant breaks, park the assignment instead of looping.
    if (submitRow) blockedAssignments.add(String(assignmentId));
    return;
  }

  const body = {
    assignment_id: assignmentId,
    responses: responseRows.map((r) => r.payload),
  };
  if (submitRow) {
    body.submit = true;
    body.skipped_questions = submitRow.payload.skipped_questions || [];
  }

  try {
    const { data } = await api.post(SYNC_ENDPOINT, body);
    const { resolvedRowIds } = partitionSyncResults(responseRows, data.results);
    const toDelete = [...resolvedRowIds];
    if (submitRow && data.submitted) toDelete.push(submitRow.id);
    if (toDelete.length) await withStore((s) => s.deleteRows(toDelete));
  } catch (err) {
    const status = err.response?.status;
    const errData = err.response?.data;
    if (!err.response) throw err; // network — abort the whole flush, retry later

    if (errData?.terminate_session || errData?.error_code === 'PARTICIPANT_DEACTIVATED') {
      // The server will never accept this participant's data again; keeping
      // the ciphertext serves nobody.
      await withStore((s) => s.clearParticipant(participantId));
      notify({ terminated: true });
      const terminated = new Error('Participant deactivated');
      terminated.terminated = true;
      throw terminated;
    }

    if (status === 400 && errData?.error_code === 'SURVEY_REQUIRED_UNANSWERED' && submitRow) {
      // Submit rolled the whole batch back. Re-send without the submit flag so
      // the answers persist; the submit intent stays queued but the assignment
      // is parked for this session — a required answer is genuinely missing
      // and re-submitting cannot fix that.
      const { submit: _s, skipped_questions: _sq, ...saveOnly } = body;
      try {
        const { data } = await api.post(SYNC_ENDPOINT, saveOnly);
        const { resolvedRowIds } = partitionSyncResults(responseRows, data.results);
        if (resolvedRowIds.length) await withStore((s) => s.deleteRows(resolvedRowIds));
      } catch (retryErr) {
        if (!retryErr.response) throw retryErr;
      }
      blockedAssignments.add(String(assignmentId));
      return;
    }

    // Survey deactivated, timepoint ordering, assignment not found, 5xx:
    // surface-not-retry per the backend contract. Rows are kept.
    console.warn(`[OFFLINE QUEUE] Sync blocked for assignment ${assignmentId}:`, errData?.error_code || status);
    blockedAssignments.add(String(assignmentId));
  }
};

export const flushQueue = async () => {
  if (flushInFlight) return flushInFlight;
  flushInFlight = (async () => {
    const participantId = getActiveParticipantId();
    if (!participantId) return;
    const rows = await withStore((s) => s.getRows(participantId)).catch(() => []);
    if (!rows.length) {
      notify({ pending: 0, syncing: false });
      return;
    }

    const byAssignment = new Map();
    for (const row of rows) {
      const key = String(row.assignmentId);
      if (blockedAssignments.has(key)) continue;
      if (!byAssignment.has(key)) byAssignment.set(key, []);
      byAssignment.get(key).push(row);
    }
    if (!byAssignment.size) return;

    notify({ syncing: true });
    try {
      for (const [assignmentId, assignmentRows] of byAssignment) {
        await flushAssignment(participantId, assignmentId, assignmentRows);
      }
    } catch (err) {
      if (!err.terminated) {
        console.debug('[OFFLINE QUEUE] Flush interrupted (still offline?):', err?.message);
      }
    } finally {
      const pending = await withStore((s) => s.countRows(participantId)).catch(() => 0);
      notify({ pending, syncing: false });
    }
  })().finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
};

// ── App-level init ──────────────────────────────────────────────────────────

let initialized = false;

export const initOfflineSync = () => {
  if (initialized) return () => {};
  initialized = true;

  const onOnline = () => flushQueue();
  window.addEventListener('online', onOnline);

  // Retry timer: cheap no-op when the queue is empty or the tab is offline.
  const interval = setInterval(() => {
    if (navigator.onLine) flushQueue();
  }, 60 * 1000);

  // Initial pass shortly after load — restores the badge and flushes anything
  // left over from a previous session.
  const kickoff = setTimeout(() => {
    refreshPending();
    if (navigator.onLine) flushQueue();
  }, 2000);

  return () => {
    window.removeEventListener('online', onOnline);
    clearInterval(interval);
    clearTimeout(kickoff);
    initialized = false;
  };
};

// Test hooks — vitest runs in jsdom without IndexedDB/WebCrypto, so tests
// inject a plain in-memory store.
export const __setStoreForTests = (impl) => {
  store = impl || secureStore;
  memoryFallback = null;
};
export const __resetForTests = () => {
  store = secureStore;
  memoryFallback = null;
  blockedAssignments.clear();
  listeners.clear();
  lastState = { pending: 0, syncing: false, terminated: false };
  flushInFlight = null;
  initialized = false;
};
